import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, Clock, Eye, Heart, User } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
import { HERO_BACKGROUNDS } from "@/data/projectImages";
import { teamMembers } from "@/data/team";
import PageHero from "@/components/PageHero";

const About = () => {
  const { t, lang } = useT();

  const values = [
    { icon: Award, key: 1 },
    { icon: Clock, key: 2 },
    { icon: Eye, key: 3 },
    { icon: Heart, key: 4 },
  ];

  // "Fassade fertig" - Referenzbild für die Über uns Seite
  const sideImage = "/img/fertige/HAUS__WhatsApp_Image_2026-07-02_at_10.02.05.jpeg";

  const teamSectionTitle = { de: "Unser Team", en: "Our team", fr: "Notre équipe", it: "Il nostro team" }[lang];
  const teamSectionSubtitle = {
    de: "Die Menschen hinter SF-Maler. Neue Fotos folgen in Kürze.",
    en: "The people behind SF-Maler. New photos coming soon.",
    fr: "Les personnes derrière SF-Maler. Nouvelles photos bientôt.",
    it: "Le persone dietro SF-Maler. Nuove foto in arrivo.",
  }[lang];
  const photoSoonLabel = { de: "Foto folgt", en: "Photo coming soon", fr: "Photo à venir", it: "Foto in arrivo" }[lang];

  return (
    <div data-testid="about-page">
      <PageHero
        overline={t.about.overline}
        title={t.about.title}
        subtitle={t.about.lead}
        backgroundImage={HERO_BACKGROUNDS.about}
      />

      <section className="py-14 sm:py-20 lg:py-28 border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-12">
          <div className="lg:col-span-5">
            <div className="aspect-[4/3] overflow-hidden bg-zinc-100">
              <img src={sideImage} alt="SF-Maler Referenzprojekt" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7 space-y-6">
            <p className="text-base text-zinc-700 leading-relaxed" data-testid="about-p1">{t.about.p1}</p>
            <p className="text-base text-zinc-700 leading-relaxed" data-testid="about-p2">{t.about.p2}</p>
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-20 lg:py-28 bg-zinc-50 border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-overline">{t.about.values_title}</span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tighter text-zinc-900 max-w-2xl">
            {t.about.values_title}
          </h2>
          <div className="mt-10 sm:mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200">
            {values.map(({ icon: Icon, key }) => (
              <div key={key} className="bg-white p-6 sm:p-8 lg:p-10" data-testid={`value-${key}`}>
                <Icon size={32} className="text-red-600" strokeWidth={1.5} />
                <h3 className="mt-5 font-display font-bold text-xl text-zinc-900">{t.about[`v${key}_title`]}</h3>
                <p className="mt-3 text-sm text-zinc-600 leading-relaxed">{t.about[`v${key}_text`]}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team preview section - photos coming soon */}
      <section className="py-14 sm:py-20 lg:py-28 border-b border-zinc-200 bg-white" data-testid="about-team-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between gap-6 flex-wrap mb-10 sm:mb-14">
            <div className="max-w-2xl">
              <span className="text-overline">{teamSectionTitle}</span>
              <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tighter text-zinc-900">
                {teamSectionTitle}
              </h2>
              <p className="mt-5 text-base text-zinc-600 leading-relaxed">{teamSectionSubtitle}</p>
            </div>
            <Link to="/team" className="text-sm font-bold text-zinc-900 inline-flex items-center gap-2 hover:text-red-600 transition-colors" data-testid="about-team-link">
              {t.nav.team} <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5 lg:gap-6">
            {teamMembers.map((m) => (
              <div key={m.id} className="group" data-testid={`about-team-${m.id}`}>
                <div className="aspect-square overflow-hidden bg-gradient-to-br from-zinc-100 to-zinc-200 border border-zinc-200 mb-3 relative flex items-center justify-center card-lift">
                  {m.photo ? (
                    <img src={m.photo} alt={m.name} className="w-full h-full object-cover img-zoom" />
                  ) : (
                    <>
                      <User size={36} className="text-zinc-400 group-hover:text-red-600 transition-colors" strokeWidth={1.2} />
                      <span className="absolute bottom-2 left-1/2 -translate-x-1/2 text-[9px] font-bold uppercase tracking-widest text-zinc-500 group-hover:text-red-600 transition-colors whitespace-nowrap">
                        {photoSoonLabel}
                      </span>
                    </>
                  )}
                </div>
                <div className="text-center">
                  <h3 className="font-display font-bold text-sm text-zinc-900 leading-tight">{m.name}</h3>
                  <p className="text-[10px] text-red-600 font-semibold mt-1 uppercase tracking-wider leading-tight">{m.role[lang]}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-20 bg-red-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tighter">
            {t.home.cta_section_title}
          </h2>
          <p className="mt-5 text-base text-red-50 max-w-2xl mx-auto">{t.home.cta_section_text}</p>
          <div className="mt-8">
            <Link to="/kontakt" className="bg-white text-red-600 px-7 py-4 font-bold inline-flex items-center gap-2 hover:bg-zinc-100 transition-colors" data-testid="about-cta">
              {t.common.cta_contact} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
